
import React, { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Lock, LogIn, Mail } from "lucide-react";
import InputField from "../ui-elements/input-field";
import Button from "../ui-elements/button";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

export interface SignInFormProps {
  onSuccess?: () => void;
  onToggleForm: () => void;
}

const SignInForm: React.FC<SignInFormProps> = ({ onSuccess, onToggleForm }) => {
  const { t } = useTranslation(['auth', 'common']);
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.email) {
      newErrors.email = t('auth:required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('auth:invalidEmail');
    }
    
    if (!formData.password) {
      newErrors.password = t('auth:required');
    } else if (formData.password.length < 6) {
      newErrors.password = t('auth:passwordMinLength');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });
      
      if (error) throw error;
      
      toast({
        title: t('common:success'),
        description: t('auth:welcomeBack'),
        duration: 3000,
      });
      
      console.log("Authentication successful:", data);
      
      // Important: Call the onSuccess callback to redirect to dashboard
      if (onSuccess) {
        console.log("Calling onSuccess to redirect to dashboard");
        onSuccess();
      }
    } catch (error: any) {
      console.error("Login error:", error);
      toast({
        title: t('common:error'),
        description: error.message || t('auth:signInToAccess'),
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSignIn} className="space-y-5">
      <InputField
        label={t('auth:email')}
        type="email"
        id="email"
        name="email"
        placeholder={t('auth:emailPlaceholder')}
        icon={<Mail className="h-4 w-4" />}
        value={formData.email}
        onChange={handleChange}
        error={errors.email}
        autoComplete="email"
        required
      />
      
      <InputField
        label={t('auth:password')}
        id="password"
        name="password"
        placeholder={t('auth:passwordPlaceholder')}
        icon={<Lock className="h-4 w-4" />}
        value={formData.password}
        onChange={handleChange}
        error={errors.password}
        showPasswordToggle
        autoComplete="current-password"
        required
      />
      
      <div className="flex justify-between items-center text-sm pt-1">
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="remember"
            className="rounded border-input h-4 w-4 text-[#E4002B] focus:ring-[#E4002B]/20"
          />
          <label htmlFor="remember" className="text-muted-foreground">
            {t('auth:rememberMe')}
          </label>
        </div>
        
        <a href="#" className="text-[#E4002B] hover:underline transition-all">
          {t('auth:forgotPassword')}
        </a>
      </div>
      
      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        isLoading={isLoading}
        rightIcon={!isLoading && <LogIn className="h-4 w-4" />}
        className="mt-8 bg-[#E4002B] hover:bg-[#c7001e]"
      >
        {t('auth:signIn')}
      </Button>

      <div className="text-center mt-8 text-sm text-muted-foreground">
        <p>
          {t('auth:notMemberYet')}{" "}
          <button 
            onClick={onToggleForm} 
            className="text-[#E4002B] hover:underline font-medium"
            type="button"
          >
            {t('auth:signUp')}
          </button>
        </p>
      </div>
    </form>
  );
};

export default SignInForm;
